import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, ShieldCheck, MessageSquare } from 'lucide-react';

const C = { accent: '#e685a5' };
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const AGES = ['18','19','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34','35'];
const DRESS = ['6','8','10','12','14','16'];
const NATIONALITIES = ['Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh','Belgium','Brazil','Bulgaria','Canada','Chile','China','Colombia','Croatia','Czech Republic','Denmark','Egypt','Finland','France','Germany','Greece','Hong Kong','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Japan','Jordan','Kenya','Korea','Kuwait','Lebanon','Malaysia','Mexico','Netherlands','New Zealand','Nigeria','Norway','Pakistan','Peru','Philippines','Poland','Portugal','Romania','Russia','Saudi Arabia','Singapore','South Africa','Spain','Sri Lanka','Sweden','Switzerland','Taiwan','Thailand','Turkey','Ukraine','United Arab Emirates','United Kingdom','United States','Vietnam','Other'];
const WORK_DONE = ['Stripping/dancing','Full Service','OnlyFans Model','No adult work experience, but I am interested'];
const WORK_INTEREST = ['All high paid adult work opportunities',"Gentlemen's club",'Strip Clubs','Cam/OF Model'];

const initial = { sex: '', email: '', name: '', phone: '', age: '', dress: '', nationality: '', experience: '', location: 'Melbourne', workDone: '', workInterest: '' };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldCls = "bg-white/5 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-1";
const selectTriggerCls = "bg-white/5 border-white/15 text-white data-[placeholder]:text-gray-500";

const ApplicationModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['sex','email','name','phone','age','dress','nationality','experience','location','workDone','workInterest'];
    for (const r of required) { if (!form[r]) return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/applications`, form);
      setSubmitted(true);
    } catch (err) {
      // Keep a local copy so nothing is lost if the server/email is unavailable
      try {
        const prev = JSON.parse(localStorage.getItem('aej_applications') || '[]');
        prev.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem('aej_applications', JSON.stringify(prev));
      } catch (_) {}
      setErrorMsg('We could not submit your application just now. Please SMS Lisa: 0429 085683.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setForm(initial); setSubmitted(false); setErrorMsg(''); };

  const SelectField = ({ label, value, onChange, options, placeholder }) => (
    <div>
      <Label className="text-gray-300 text-xs mb-1.5 block">{label} <span style={{ color: C.accent }}>*</span></Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-64">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#121218] border-white/10 text-white" data-testid="application-modal">
        {submitted ? (
          <div className="text-center py-8" data-testid="application-success">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: `${C.accent}20`, color: C.accent }}>
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold mb-2" style={serif}>Application Received</h3>
            <p className="text-gray-400 text-sm max-w-sm mx-auto">Thank you, {form.name.split(' ')[0]}. Your confidential application has been recorded. This is a demo form, so nothing is actually sent yet.</p>
            <p className="text-gray-300 text-sm mt-4">Any questions? Please SMS Lisa: <span className="font-semibold" style={{ color: C.accent }}>0429 085683</span> (no direct telephone calls).</p>
            <Button className="mt-6 text-white font-semibold" style={{ background: C.accent }} onClick={reset}>Submit Another</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl" style={serif}>Confidential Application</DialogTitle>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: C.accent }}>Apply for Legal Jobs</p>
              <p className="flex items-center gap-1.5 text-[11px] text-gray-400 pt-1"><ShieldCheck className="w-3.5 h-3.5" /> Confidentiality assured</p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="application-form">
              {/* Sex */}
              <div>
                <Label className="text-gray-300 text-xs mb-2 block">Sex <span style={{ color: C.accent }}>*</span></Label>
                <RadioGroup value={form.sex} onValueChange={set('sex')} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="M" id="sex-m" className="border-white/40 text-white" />
                    <Label htmlFor="sex-m" className="text-gray-200 text-sm cursor-pointer">M</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="F" id="sex-f" className="border-white/40 text-white" />
                    <Label htmlFor="sex-f" className="text-gray-200 text-sm cursor-pointer">F</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label className="text-gray-300 text-xs mb-1.5 block">Name <span style={{ color: C.accent }}>*</span></Label>
                <Input required value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Full name" className={fieldCls} data-testid="app-name" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs mb-1.5 block">Email <span style={{ color: C.accent }}>*</span></Label>
                <Input required type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder="you@email.com" className={fieldCls} data-testid="app-email" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs mb-1.5 block">Phone Number <span style={{ color: C.accent }}>*</span></Label>
                <Input required type="tel" value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="Mobile number" className={fieldCls} data-testid="app-phone" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <SelectField label="Age" value={form.age} onChange={set('age')} options={AGES} placeholder="Age" />
                <SelectField label="Dress Size" value={form.dress} onChange={set('dress')} options={DRESS} placeholder="Dress Size" />
              </div>

              <SelectField label="Nationality" value={form.nationality} onChange={set('nationality')} options={NATIONALITIES} placeholder="Nationality" />

              <div className="grid grid-cols-2 gap-4">
                <SelectField label="Adult Industry Experience" value={form.experience} onChange={set('experience')} options={['Yes','No']} placeholder="Experience" />
                <SelectField label="Location" value={form.location} onChange={set('location')} options={['Sydney','Melbourne']} placeholder="Location" />
              </div>

              <SelectField label="What type of adult work have you done?" value={form.workDone} onChange={set('workDone')} options={WORK_DONE} placeholder="Select an option" />
              <SelectField label="What type of adult work are you interested to know about?" value={form.workInterest} onChange={set('workInterest')} options={WORK_INTEREST} placeholder="Select an option" />

              <Button type="submit" disabled={submitting} className="w-full text-white font-bold h-12 tracking-wide uppercase disabled:opacity-70" style={{ background: C.accent }} data-testid="app-submit">
                {submitting ? 'Submitting…' : 'Apply for Jobs Now'}
              </Button>

              {errorMsg && (
                <p className="text-[12px] text-red-400 text-center" data-testid="app-error">{errorMsg}</p>
              )}

              <p className="flex items-start gap-1.5 text-[11px] text-gray-500 leading-relaxed">
                <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: C.accent }} />
                Any questions? Please SMS Lisa: 0429 085683 (no direct telephone calls).
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationModal;
